// What a branch history's page reads from GitHub (docs/design.md "Branch
// histories"): where a `commits/<spec>` URL's ref ends (`resolveRef`), the
// ref's tip (`refTip`), its commits a window at a time from that tip
// (`fetchWindow`), its force-pushes from the repository's activity
// (`fetchForcePushes`, the one bridge rule a history needs) and the
// commits each replaced (`replacedRun`), the files its window changes
// (`windowFiles`), and each commit's pull requests and checks
// (`fetchCommitFacts`). Functions of what they read and an AbortSignal, as
// a compare's (compare/fetch.ts).
//
// Resolution is one GraphQL request whatever the spec's length: every
// prefix of the spec that git takes as a name is asked for as a branch and
// as a tag (aliased `ref(qualifiedName:)` fields), with the default branch
// and, when the first component looks like one, a commit SHA. The longest
// existing ref wins, a branch before a tag of the same name; then the SHA;
// the rest of the spec is the path the history is filtered to. GitHub's
// own `commits/` page reads its URL the same way.
//
// A window is GitHub's `commits?sha=<tip>&path=<path>`, newest first, 100
// to a page. Every page of one history is read from the tip's SHA, never
// the branch's name, so a push between pages cannot mix two histories: a
// push is a new tip, read from its first page again.
import { graphql, rest } from '../../api/gh';
import { HttpError } from '../../api/cache';
import type { PrFile } from '../../api/queries';
import type { ChecksState } from '../../logic/checks';
import type { ForcePush, ReplacedRun } from '../../logic/refTrack';
import { EMPTY_TREE } from '../../logic/track';
export type { ForcePush, ReplacedRun };
import { inPath, isRefName, isRefPath, type RefLocator, type RefSubject } from '../../subject';
import { completeFiles, readCommit, readLinked, treeOf, type CompareCommit, type CompareReads, type LinkedPull } from '../compare/fetch';

/** Commits per window (GitHub's most per page). */
export const REF_PAGE = 100;
/** The most windows a page holds: older history than REF_PAGE × MAX_REF_PAGES commits is not read. */
export const MAX_REF_PAGES = 10;
/** The most force-pushes read of a branch's activity (the newest). */
export const MAX_FORCE_PUSHES = 10;
/** The most commits of one force-push's replaced history shown (the newest), as a pull request's lookup. */
export const MAX_REPLACED_PER_PUSH = 10;
/** The most components of a spec asked for as a ref: a longer branch name is not looked for. */
export const MAX_REF_COMPONENTS = 32;

/** What a ref is: a branch, a tag, or a commit named by its SHA. */
export type RefKind = 'branch' | 'tag' | 'commit';

/** A `commits/<spec>` URL resolved against the repository. */
export interface ResolvedRef {
  subject: RefSubject;
  kind: RefKind;
  /** The commit the ref pointed at when resolved. */
  sha: string;
  /** The repository's default branch, when GitHub said. */
  defaultBranch: string | null;
}

/** Why a history cannot be shown: GitHub has no such ref (or does not show the repository). */
export class RefNotFound extends Error {}

const str = (v: unknown): string | null => (typeof v === 'string' ? v : null);
const SHA_LIKE = /^[0-9a-fA-F]{7,40}$/;

/** The GraphQL fields of a ref: its target's commit, through one annotated tag. */
const REF_FIELDS = 'name target { __typename oid ... on Tag { target { __typename oid } } }';

/** The commit a ref's target names (a commit, or an annotated tag of one), or null. */
function commitOf(ref: unknown): string | null {
  const t = (ref as { target?: { __typename?: unknown; oid?: unknown; target?: { __typename?: unknown; oid?: unknown } } } | null)?.target;
  if (t?.__typename === 'Commit') return str(t.oid);
  if (t?.__typename === 'Tag' && t.target?.__typename === 'Commit') return str(t.target.oid);
  return null;
}

/** The query resolving `names` (each asked as a branch, `h<i>`, and a tag, `t<i>`), and a SHA when `sha`. */
export function resolveQuery(names: number, sha: boolean): string {
  const vars = [...Array.from({ length: names }, (_, i) => `$h${i}: String!, $t${i}: String!`), ...(sha ? ['$sha: String!'] : [])];
  const fields = [
    `defaultBranchRef { ${REF_FIELDS} }`,
    ...Array.from({ length: names }, (_, i) => `h${i}: ref(qualifiedName: $h${i}) { ${REF_FIELDS} }\n    t${i}: ref(qualifiedName: $t${i}) { ${REF_FIELDS} }`),
    ...(sha ? ['sha: object(expression: $sha) { __typename oid }'] : []),
  ];
  return `query RefResolve($owner: String!, $repo: String!${vars.length ? ', ' + vars.join(', ') : ''}) {\n  repository(owner: $owner, name: $repo) {\n    ${fields.join('\n    ')}\n  }\n}`;
}

/**
 * The ref subject `locator` names (see the file comment), with what the
 * ref is and its commit now. `HEAD` as the spec's first component is the
 * default branch. Throws RefNotFound when no branch, tag or commit of the
 * repository matches, or the rest is no path (`isRefPath`).
 */
export async function resolveRef(locator: RefLocator, opts: CompareReads): Promise<ResolvedRef> {
  const { owner, repo } = locator;
  // A locator that says where its ref ends names the ref whole, its path apart.
  const exact = locator.path !== undefined;
  const parts = [...locator.spec.split('/'), ...(exact ? locator.path!.split('/') : [])];
  // `HEAD` is the default branch: GitHub's first component, or (where the locator says where the ref ends) the whole spec.
  const head = exact ? locator.spec === 'HEAD' : parts[0] === 'HEAD';
  // Every prefix git takes as a name, longest last; only the whole spec for an exact one.
  const names: string[] = [];
  if (!head && exact) names.push(locator.spec);
  else if (!head) for (let n = 1; n <= Math.min(parts.length, MAX_REF_COMPONENTS); n++) {
    const name = parts.slice(0, n).join('/');
    if (isRefName(name)) names.push(name);
  }
  const sha = !head && SHA_LIKE.test(parts[0]) && (!exact || !locator.spec.includes('/'));
  const vars: Record<string, unknown> = { owner, repo };
  names.forEach((name, i) => {
    vars['h' + i] = `refs/heads/${name}`;
    vars['t' + i] = `refs/tags/${name}`;
  });
  if (sha) vars.sha = parts[0];
  const res = await graphql<{ repository: Record<string, unknown> | null }>(resolveQuery(names.length, sha), vars, opts);
  const r = res?.repository;
  if (!r) throw new RefNotFound(`GitHub doesn't show ${owner}/${repo} to you.`);
  const dflt = str((r.defaultBranchRef as { name?: unknown } | null)?.name);
  const found = (ref: string, kind: RefKind, at: string | null, used: number): ResolvedRef => {
    const rest_ = parts.slice(used).join('/');
    if (!at || (rest_ && !isRefPath(rest_))) throw notFound();
    return { subject: { kind: 'ref', owner, repo, ref, path: rest_ || null }, kind, sha: at, defaultBranch: dflt };
  };
  const notFound = () =>
    new RefNotFound(head ? `GitHub says ${owner}/${repo} has no default branch.` : `${owner}/${repo} has no branch, tag or commit named ${parts[0]}${parts.length > 1 ? ' (or any longer name in this address)' : ''}.`);
  if (head) {
    if (!dflt) throw notFound();
    return found(dflt, 'branch', commitOf(r.defaultBranchRef), 1);
  }
  for (let i = names.length - 1; i >= 0; i--) {
    const used = names[i].split('/').length;
    for (const [alias, kind] of [['h', 'branch'], ['t', 'tag']] as const) {
      const at = commitOf(r[alias + i]);
      if (at) return found(names[i], kind, at, used);
    }
  }
  const obj = r.sha as { __typename?: unknown; oid?: unknown } | null | undefined;
  if (sha && obj?.__typename === 'Commit' && str(obj.oid)) {
    const full = str(obj.oid)!.toLowerCase();
    return found(full, 'commit', full, 1);
  }
  throw notFound();
}

/**
 * What a resolved subject's ref is and the commit it points at now, read
 * as exactly that ref (a branch before a tag of its name; a full SHA a
 * commit), never by a longer name: a key keeps its meaning whatever the
 * repository gains. One GraphQL request. Throws RefNotFound when the
 * repository has no such ref (any more).
 */
export async function refKind(subject: RefSubject, opts: CompareReads): Promise<{ kind: RefKind; sha: string; defaultBranch: string | null }> {
  const sha = /^(?:[0-9a-f]{40}|[0-9a-f]{64})$/.test(subject.ref);
  const vars: Record<string, unknown> = { owner: subject.owner, repo: subject.repo, h0: `refs/heads/${subject.ref}`, t0: `refs/tags/${subject.ref}`, ...(sha ? { sha: subject.ref } : {}) };
  const r = (await graphql<{ repository: Record<string, unknown> | null }>(resolveQuery(1, sha), vars, opts))?.repository;
  if (!r) throw new RefNotFound(`GitHub doesn't show ${subject.owner}/${subject.repo} to you.`);
  const defaultBranch = str((r.defaultBranchRef as { name?: unknown } | null)?.name);
  for (const [alias, kind] of [['h0', 'branch'], ['t0', 'tag']] as const) {
    const at = commitOf(r[alias]);
    if (at) return { kind, sha: at, defaultBranch };
  }
  const obj = r.sha as { __typename?: unknown; oid?: unknown } | null | undefined;
  if (sha && obj?.__typename === 'Commit') return { kind: 'commit', sha: subject.ref, defaultBranch };
  throw new RefNotFound(`${subject.owner}/${subject.repo} has no branch, tag or commit ${subject.ref}.`);
}

/**
 * The commit `subject`'s ref points at now (a branch's or tag's tip; a
 * commit is itself), read as exactly that ref: a branch made since with a
 * longer name never changes what a resolved subject names. Throws
 * RefNotFound when the ref is gone (a deleted branch).
 */
export async function refTip(subject: RefSubject, kind: RefKind, opts: CompareReads): Promise<string> {
  if (kind === 'commit') return subject.ref;
  const q = `query RefTip($owner: String!, $repo: String!, $ref: String!) {\n  repository(owner: $owner, name: $repo) { ref(qualifiedName: $ref) { ${REF_FIELDS} } }\n}`;
  const res = await graphql<{ repository: { ref: unknown } | null }>(q, { owner: subject.owner, repo: subject.repo, ref: `refs/${kind === 'branch' ? 'heads' : 'tags'}/${subject.ref}` }, opts);
  const at = commitOf(res?.repository?.ref);
  if (!at) throw new RefNotFound(`GitHub no longer has the ${kind} ${subject.ref} in ${subject.owner}/${subject.repo}.`);
  return at;
}

/** One window of a history: its commits, newest first, and whether older ones exist. */
export interface HistoryWindow {
  commits: CompareCommit[];
  /** More commits exist past this window (a full page whose oldest commit has a parent). */
  more: boolean;
}

/**
 * Window `page` (1 the newest) of the history from commit `tip`, filtered
 * to `subject.path` when it has one: GitHub's `commits?sha=&path=`, which
 * lists newest first, as `git log` does.
 */
export async function fetchWindow(subject: RefSubject, tip: string, page: number, opts: CompareReads): Promise<HistoryWindow> {
  const path = subject.path === null ? '' : `&path=${encodeURIComponent(subject.path)}`;
  const list = await rest<unknown>(`repos/${subject.owner}/${subject.repo}/commits?sha=${tip}${path}&per_page=${REF_PAGE}&page=${page}`, opts);
  const commits = (Array.isArray(list) ? list : []).map(readCommit).filter((c): c is CompareCommit => c !== null);
  const oldest = commits[commits.length - 1];
  return { commits, more: commits.length === REF_PAGE && !!oldest?.parents.length };
}

/**
 * The branch's newest MAX_FORCE_PUSHES force-pushes, newest first
 * (`repos/o/r/activity?ref=refs/heads/<branch>&activity_type=force_push`),
 * as far back as GitHub keeps its activity: Changeling stores none.
 */
export async function fetchForcePushes(owner: string, repo: string, branch: string, opts: CompareReads): Promise<ForcePush[]> {
  const ref = encodeURIComponent(`refs/heads/${branch}`);
  const list = await rest<unknown>(`repos/${owner}/${repo}/activity?ref=${ref}&activity_type=force_push&direction=desc&per_page=${MAX_FORCE_PUSHES}`, opts);
  return (Array.isArray(list) ? list : []).flatMap((a): ForcePush[] => {
    const x = a as { before?: unknown; after?: unknown; activity_type?: unknown; timestamp?: unknown; actor?: { login?: unknown } | null };
    const before = str(x.before), after = str(x.after);
    // A push from nothing (a branch made by it) replaced nothing.
    if (x.activity_type !== 'force_push' || !before || !after || /^0+$/.test(before)) return [];
    return [{ before: before.toLowerCase(), after: after.toLowerCase(), actor: str(x.actor?.login), at: str(x.timestamp) }];
  }).slice(0, MAX_FORCE_PUSHES);
}

/**
 * What the force-push to `before` replaced, against the branch at `tip`:
 * GitHub's compare `tip...before` (the commits `before` has and the tip
 * does not, and their merge base, the fork point). A `before` GitHub no
 * longer has (garbage collected) is itself alone, with no fork point.
 */
export async function replacedRun(owner: string, repo: string, tip: string, before: string, opts: CompareReads): Promise<ReplacedRun> {
  try {
    const c = await rest<{ merge_base_commit?: { sha?: unknown } | null; commits?: unknown }>(`repos/${owner}/${repo}/compare/${tip}...${before}?per_page=${MAX_REPLACED_PER_PUSH}`, opts);
    const commits = (Array.isArray(c.commits) ? c.commits : []).map(readCommit).filter((x): x is CompareCommit => x !== null);
    return { forkPoint: str(c.merge_base_commit?.sha), commits: commits.slice(Math.max(0, commits.length - MAX_REPLACED_PER_PUSH)) };
  } catch (e) {
    if (!(e instanceof HttpError) || e.status !== 404) throw e;
    return { forkPoint: null, commits: [{ sha: before, parents: [], message: '', author: null, committed: null }] };
  }
}

/** The files a window changes, base → head: what its Files view lists for All. */
export interface WindowFiles {
  files: PrFile[];
  /** Files past GitHub's 300 that could not be listed (a tree too large to read whole); 0 when every file is listed. */
  missing: number | null;
  totals: { added: number; removed: number };
}

/**
 * The files changed from `base` to `head` in `subject.path` (every file
 * without one): GitHub's compare, its files past 300 from the trees
 * (`completeFiles`, as a compare's). From the empty tree (a window
 * reaching the root commit) GitHub compares nothing, so they are the
 * head's tree, every file added, with no patch.
 */
export async function windowFiles(subject: RefSubject, base: string, head: string, opts: CompareReads): Promise<WindowFiles> {
  const { owner, repo } = subject;
  let files: PrFile[];
  let missing: number | null = 0;
  if (base === EMPTY_TREE) {
    const tree = await treeOf(owner, repo, head, opts);
    files = [...(tree ?? new Map<string, string>())].map(([filename, sha]) => ({ filename, sha, status: 'added', additions: 0, deletions: 0, changes: 1 }));
    if (!tree) missing = null;
  } else {
    const c = await rest<{ files?: unknown }>(`repos/${owner}/${repo}/compare/${base}...${head}?per_page=1`, opts);
    const listed = (Array.isArray(c.files) ? (c.files as PrFile[]) : []).filter((f) => typeof f?.filename === 'string');
    ({ files, missing } = await completeFiles(owner, repo, base, head, listed, opts));
  }
  files = files.filter((f) => inPath(subject.path, f.filename) || (!!f.previous_filename && inPath(subject.path, f.previous_filename)));
  const n = (v: unknown) => (typeof v === 'number' && Number.isFinite(v) ? v : 0);
  return { files, missing, totals: files.reduce((t, f) => ({ added: t.added + n(f.additions), removed: t.removed + n(f.deletions) }), { added: 0, removed: 0 }) };
}

/** What a history shows beside each commit: the pull requests that brought it in, and its checks. */
export interface CommitFacts {
  pulls: LinkedPull[];
  checks: ChecksState;
}

/** Commits per GraphQL request. */
const FACTS_BATCH = 50;
/** Pull requests per commit. */
const PULLS_PER_COMMIT = 3;

function factsQuery(n: number): string {
  const fields = Array.from(
    { length: n },
    (_, i) => `c${i}: object(oid: $c${i}) { ... on Commit { statusCheckRollup { state } associatedPullRequests(first: ${PULLS_PER_COMMIT}) { nodes { number title state url } } } }`,
  ).join('\n    ');
  const vars = Array.from({ length: n }, (_, i) => `$c${i}: GitObjectID!`).join(', ');
  return `query RefCommitFacts($owner: String!, $repo: String!, ${vars}) {\n  repository(owner: $owner, name: $repo) {\n    ${fields}\n  }\n}`;
}

/** GitHub's rollup state of a commit's check runs and statuses, read by the one rule of logic/checks.ts. */
export function rollupState(state: unknown): ChecksState {
  return state === 'SUCCESS' ? 'success' : state === 'FAILURE' || state === 'ERROR' ? 'failure' : state === 'PENDING' || state === 'EXPECTED' ? 'pending' : 'none';
}

/**
 * Each of `shas`' pull requests (PULLS_PER_COMMIT) and checks (GitHub's
 * rollup state), FACTS_BATCH commits to a GraphQL request. A commit
 * GitHub says nothing of has none.
 */
export async function fetchCommitFacts(owner: string, repo: string, shas: readonly string[], opts: CompareReads): Promise<Map<string, CommitFacts>> {
  const out = new Map<string, CommitFacts>();
  for (let i = 0; i < shas.length; i += FACTS_BATCH) {
    const chunk = shas.slice(i, i + FACTS_BATCH);
    const vars: Record<string, unknown> = { owner, repo };
    chunk.forEach((sha, j) => (vars['c' + j] = sha));
    const res = await graphql<{ repository: Record<string, { statusCheckRollup?: { state?: unknown } | null; associatedPullRequests?: { nodes?: unknown[] } } | null> | null }>(factsQuery(chunk.length), vars, {
      ...opts,
      partial: true,
    });
    chunk.forEach((sha, j) => {
      const c = res?.repository?.['c' + j];
      out.set(sha, { pulls: readLinked(c?.associatedPullRequests?.nodes ?? []), checks: rollupState(c?.statusCheckRollup?.state) });
    });
  }
  return out;
}
